import React, {useCallback, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import {selectSearchRequest} from 'src/components/criteria-strip/services/criteria-strip-slice';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {ErrorPage} from 'src/modules/error-page/error-page.components';
import {
    UserSearchResultController,
    UserSearchResultView,
    selectSelectedColumns,
    userSearchResultStateActions,
} from 'src/modules/user-search-result';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import Button from '@pb/kit-component-button';
import {Item} from '@pb/kit-component-group';
import {StickyElement} from '@pb/kit-component-sticky-element';


import {UserSearchResultHeader} from './components/header';
import {UsersSearchToolsRibbon} from './components/users-search-tools-ribbon';

type Props = {
    searchId: string,
};

const PLACE = 'User Search Result';

export const UserSearchResult = ({searchId}: Props) => {
    const dispatch = useDispatch();
    const userSearchCriteria = useSelector(selectSearchRequest);
    const navigate = useNavigate();
    const {searchCriteriaOpen} = useEventTracking();
    const searchRequest = useSelector(selectSearchRequest);
    const selectedColumns = useSelector(selectSelectedColumns);

    useEffect(() => {
        if (searchRequest) {
            dispatch(userSearchResultStateActions.setSearchCriteria(searchRequest));
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffectWithDeps((prev, current) => {
        if (current.searchRequest && !isDeepEqual({prev, current})) {
            dispatch(userSearchResultStateActions.setSearchCriteria(current.searchRequest));
        }
    }, {searchRequest, selectedColumns});

    const onOpenCriteria = useCallback(() => {
        searchCriteriaOpen({place: 'Modify Criteria Button'});
        navigate(`/search-criteria/${searchId}/users`);
    }, [searchId, navigate, searchCriteriaOpen]);

    const noCriteriaView = useMemo(() => {
        return (
            <ErrorPage
                title="The empty search criteria is not allowed"
                subtitle="Please specify search criteria"
                secondaryButton={(
                    <Button variant="Type.SECONDARY" onClick={onOpenCriteria}>
                        Modify Criteria
                    </Button>
                )}
            />
        );
    }, [onOpenCriteria]);

    if (!userSearchCriteria) {
        return null;
    }

    return (
        <UserSearchResultController place={PLACE} noCriteriaView={noCriteriaView}>
            {props => (
                <Item grow={1}>
                    <UserSearchResultHeader searchId={searchId} totalCount={props.totalCount}/>
                    <StickyElement className="sticky-tool-ribbon">
                        <UsersSearchToolsRibbon
                            place={PLACE}
                            adminId={props.adminId}
                            isLoading={props.isLoading}
                            getAllEntityIds={props.getAllEntityIds}
                            templates={props.templates}
                            activeSearchTemplate={props.activeSearchTemplate}
                            setActiveSearchTemplate={props.setActiveSearchTemplate}
                            loadTemplates={props.loadTemplates}
                            loadActiveSearchTemplate={props.loadActiveSearchTemplate}
                            refreshResults={props.refreshResults}
                        />
                    </StickyElement>
                    <UserSearchResultView
                        getAllEntityIds={props.getAllEntityIds}
                        place={PLACE}
                        activeSearchTemplate={props.activeSearchTemplate}
                    />
                </Item>
            )}
        </UserSearchResultController>
    );
};
